/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { serviceCaseSearchSuggestions } from './ServiceCaseModel';
import { licenseSearchSuggestions } from './LicenseModel';
import { userSearchSuggestions, userroleSearchSuggestions } from './UserModel';
import { trainingSearchSuggestions, productTrainingSearchSuggestions } from './AuthorizationModel';
import {
    teamTrainingRoute, teamproductRoute, alltrainingRoute, allproductRoute, authorizationRoute,
    manualRoute, bulletinRoute, GSInfoRoute, laborTimeGuideRoute, downloadsRoute, instrumentConstantsRoute
} from './constants';

export enum CategoryType {
    ServiceCase,
    License,
    User,
    UserRole,
    TrainingAuthorization,
    ProductAuthorization,
    ServiceManual,
    ServiceBulletin,
    GSInfo,
    Downloads,
    LaborTimeGuide,
    InstrumentConstants
}

export interface Category {
  id: number,
  name: string,
  isDateDisabled?: boolean,
  requestBodyforChart: string,
  requestBodyforChartFiltered?: string,
  requestBodyforColChart?: string,
  requestBodyfoPieChart?: string,
  requestBodyForGrid: string,
  requestBodyForExport: string,
  chartLabel?: string,
  xaxisLabel?: string,
  yaxisLabel?: string,
  isAssignToEnabled?: boolean,
  isCheckBoxEnabled?: boolean,
  isExportEnabled: boolean,
  isEditEnabled?: boolean,
  isSelectOneOnly?: boolean,
  isOpenEnabled?: boolean,
  columns: any,
  filters: Function,
  type: CategoryType,
  requestBodyforProductChart?: string,
  requestBodyforUserChart?: string,
  route?: string,
  moduleName: string,
  requestBodyForGridTotalCount?: string
}

function getFilters(suggestions: any, columns: any) {
  return suggestions.filter((suggestion: any) => {
    return columns.find((x: any) => {
      return suggestion.searchProp == x.name
    }) != undefined
  })
}

export const categories: Array<Category> = [
  {
    id: 0,
    name: 'My Open Cases',
    moduleName:'Open Service cases Report',
    isDateDisabled: true,
    requestBodyforChart: 'query{ myopenservicecases { count data { id, opendate,age ,instrument { partnumber,description,productmodel { product {productline, displayname,imagename,twodproductname{name} } } }} } }',
    requestBodyForGrid: 'query{ myopenservicecases %s { count data { id,serialnumber,opendate,closeddate,age,failuredescription,status,summary {repairdescription, repairtime, },instrument { partnumber,description,productmodel { product { displayname } } } assigneduser { firstname,lastname } } } }',
        requestBodyForGridTotalCount: 'query{ myopenservicecases %s { count } }',
        requestBodyForExport: 'query{ myopenservicecases %s { count data %r } }',
    chartLabel: 'Days Open',
    xaxisLabel: 'Case Number',
    yaxisLabel: 'Days Open',
    isAssignToEnabled: true,
    isCheckBoxEnabled: true,
    isExportEnabled: true,
    isOpenEnabled: true,
    columns: [
      { name: 'id', title: 'Case ID', display: true},
      { name: 'serialnumber', title: 'Serial No.', display: true},
      { name: 'partnumber', title: 'Part No.', display: true},
      { name: 'description', title: 'Description', width: 150, display: true},
      { name: 'opendate', title: 'Open Date', display: true},
      { name: 'failuredescription', title: 'Failure Description', width: 150, display: true},
      {
        name: 'repairdescription',
        title: 'Detailed Description of service',
        width: 150, display: true
      },
      { name: 'repairtime', title: 'Repair Time', display: true },
      { name: 'age', title: 'Days Open', display: true}
    ],
    filters () { return getFilters(serviceCaseSearchSuggestions, this.columns); },
    type: CategoryType.ServiceCase
  },
  {
    id: 1,
    name: 'My Closed Cases',
    moduleName: 'Closed Service cases Report',
    isDateDisabled: false,
    requestBodyforChart: 'query($closedfromdate: Date, $closedtodate: Date){ myclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate) { count data { id, opendate,age ,instrument { partnumber,description,productmodel { product {productline, displayname,imagename,twodproductname{name} } } }} }}',
    requestBodyForGrid: 'query($closedfromdate: Date, $closedtodate: Date){ myclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count data { id,serialnumber,opendate,closeddate,age,failuredescription,status,summary {repairdescription, repairtime },instrument { partnumber,description,productmodel { product { displayname } } } ,assigneduser { firstname,lastname }, closedbyuser { firstname,lastname } } } }',
      requestBodyForGridTotalCount: 'query($closedfromdate: Date, $closedtodate: Date){ myclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count } }',
      requestBodyForExport: 'query($closedfromdate: Date, $closedtodate: Date){ myclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count data %r } }',
    chartLabel: 'Days Open',
    xaxisLabel: 'Case Number',
    yaxisLabel: 'Days Open',
    isAssignToEnabled: false,
    isCheckBoxEnabled: true,
    isSelectOneOnly: true,
    isExportEnabled: true,
    isOpenEnabled: true,
    columns: [
      { name: 'id', title: 'Case ID', display: true},
      { name: 'serialnumber', title: 'Serial No.', display: true },
      { name: 'partnumber', title: 'Part No.', display: true},
      { name: 'description', title: 'Description', width: 150, display: true},
      { name: 'opendate', title: 'Open Date', display: true},
      { name: 'closeddate', title: 'Closed Date', display: true},
      { name: 'failuredescription', title: 'Failure Description', width: 150, display: true },
      {
        name: 'repairdescription',
        title: 'Detailed Description of service',
        width: 150
        , display: true
      },
      { name: 'repairtime', title: 'Repair Time', display: true },
      { name: 'age', title: 'Days Open', display: true}
    ],
    filters () { return getFilters(serviceCaseSearchSuggestions, this.columns); },
    type: CategoryType.ServiceCase
  },
  {
    id: 2,
    name: 'Team Open Cases',
    moduleName: 'Team Open Service cases Report',
    isDateDisabled: true,
    requestBodyforChart: 'query{ mycompanyopenservicecases { count data { id,age,instrument { partnumber,description,productmodel { product {productline, displayname,imagename,twodproductname{name} } } },assigneduser { firstname,lastname } } } }',
    requestBodyForGrid: 'query{ mycompanyopenservicecases %s { count data { id,serialnumber,opendate,closeddate,age,failuredescription,status,summary {repairdescription,repairtime },instrument { partnumber,description,productmodel { product { displayname } } } assigneduser { firstname,lastname } } } }',
      requestBodyForGridTotalCount: 'query{ mycompanyopenservicecases %s { count } }',
      requestBodyForExport: 'query{ mycompanyopenservicecases %s { count data %r } }',
    chartLabel: 'Number of Cases',
    xaxisLabel: 'Team Members',
    yaxisLabel: 'Number of Cases',
    isAssignToEnabled: true,
    isCheckBoxEnabled: true,
    isExportEnabled: true,
    isOpenEnabled: true,
    columns: [
      { name: 'id', title: 'Case ID', display: true },
      { name: 'serialnumber', title: 'Serial No.', display: true },
      { name: 'partnumber', title: 'Part No.', display: true },
      { name: 'description', title: 'Description', width: 150, display: true },
      { name: 'username', title: 'Name', display: true },
      { name: 'opendate', title: 'Open Date', display: true },
      { name: 'failuredescription', title: 'Failure Description', width: 150, display: true },
      {
        name: 'repairdescription',
        title: 'Detailed Description of service',
        width: 150, display: true
      },
      { name: 'repairtime', title: 'Repair Time', display: true },
      { name: 'age', title: 'Days Open', display: true }
    ],
    filters () { return getFilters(serviceCaseSearchSuggestions, this.columns); },
    type: CategoryType.ServiceCase
  },
  {
    id: 3,
    name: 'Team Closed Cases',
    moduleName: 'Team Closed Service cases Report',
    isDateDisabled: false,
    requestBodyforChart: 'query($closedfromdate: Date, $closedtodate: Date){ mycompanyclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate) {  count data { id,age,status,instrument { partnumber,description,productmodel { product {productline, displayname,imagename,twodproductname{name} } } },closedbyuser { firstname,lastname },assigneduser { firstname,lastname } } } }',
    requestBodyForGrid: 'query($closedfromdate: Date, $closedtodate: Date){ mycompanyclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count data { id,serialnumber,opendate,closeddate,status,age,failuredescription,summary { repairdescription,repairtime },instrument { partnumber,description,productmodel { product { displayname } } } ,assigneduser { firstname,lastname }, closedbyuser { firstname,lastname } } } }',
      requestBodyForExport: 'query($closedfromdate: Date, $closedtodate: Date){ mycompanyclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count data %r } }',
      requestBodyForGridTotalCount: 'query($closedfromdate: Date, $closedtodate: Date){ mycompanyclosedservicecases(closedfromdate: $closedfromdate, closedtodate: $closedtodate%s) { count } }',
    chartLabel: 'Number of Cases',
    xaxisLabel: 'Team Members',
    yaxisLabel: 'Number of cases',
    isAssignToEnabled: false,
    isCheckBoxEnabled: true,
    isSelectOneOnly: true,
    isExportEnabled: true,
    isOpenEnabled: true,
    columns: [
      { name: 'id', title: 'Case ID', display:true},
      { name: 'serialnumber', title: 'Serial No.', display: true },
      { name: 'partnumber', title: 'Part No.', display: true},
      { name: 'description', title: 'Description', width: 150, display: true },
      { name: 'username', title: 'Name', display: true},
      { name: 'opendate', title: 'Open Date', display: true},
      { name: 'closeddate', title: 'Closed Date', display: true},
      { name: 'failuredescription', title: 'Failure Description', width: 150, display: true},
      {
        name: 'repairdescription',
        title: 'Detailed Description of service',
        width: 150,
        display: true
      },
      { name: 'repairtime', title: 'Repair Time', display: true },
      { name: 'age', title: 'Days Open', display: true}
    ],
    filters () { return getFilters(serviceCaseSearchSuggestions, this.columns); },
    type: CategoryType.ServiceCase
  }, {
    id: 4,
    name: 'License',
    moduleName: 'License Report',
    isDateDisabled: true,
    requestBodyforChart: 'query{ mylicensechartsummary { count barChartData { licenseid licensetype{ typename } machinename user{ firstname lastname company{ companyname } } company{ companyname } subscription{ enddate } } } }',
    requestBodyforChartFiltered: 'query{ mylicensechartsummary %s { count barChartData { licenseid licensetype{ typename } machinename user{ firstname lastname company{ companyname } } company{ companyname } subscription{ enddate } } } }',
      requestBodyForGrid: 'query{ mylicenses %s {count data{ licenseid ,licensetype{typename},machinename,user{emailid,firstname,lastname,company{companyname}},company{companyname}, activationcode{code}, orderid, licensestatus, activationcount, subscription{startdate,enddate}}}}',
      requestBodyForGridTotalCount: 'query{ mylicenses %s { count } }',
      requestBodyForExport:'',
    chartLabel: 'Licenses',
    xaxisLabel: 'Users',
    yaxisLabel: 'Expires in (days)',
    isAssignToEnabled: false,
    isCheckBoxEnabled: false,
    isExportEnabled: true,
    columns: [
        { name: 'username', title: 'Name', display: true },
        { name: 'emailid', title: 'Email', display: true },
      { name: 'company', title: 'Company Name', display: true },
      { name: 'machinename', title: 'Machine Name', display: true },
      { name: 'code', title: 'Activation code', display: true },
        { name: 'license', title: 'License', width: 150, display: true},
      { name: 'enddate', title: 'Expiry Date', width: 150, display: true}
    ],
    filters () {return getFilters(licenseSearchSuggestions, this.columns);  },
    type: CategoryType.License
    },
    {
    id: 5,
    name: 'All Service Cases',
    moduleName: 'All Service cases Report',
    isDateDisabled: false,
    requestBodyforProductChart: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count data { productLine,twodproductname, numberOfCases } } }',
    requestBodyforUserChart: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count data { firstName,lastName, openCases,closedCases} } }',
    requestBodyforChart: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count data { id, opendate,closeddate,age,status ,instrument { partnumber,description,productmodel { product {productline, displayname,imagename } } },assigneduser { firstname,lastname }, closedbyuser { firstname,lastname }} } }',
    requestBodyForGrid: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count data %r } }',
    requestBodyForExport: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count data %r } }',
    requestBodyForGridTotalCount: 'query($updatedfromdate: Date, $updatedtodate: Date){ allservicecases(updatedfromdate: $updatedfromdate, updatedtodate: $updatedtodate%s) { count } }',
    chartLabel: 'Number of Cases',
    xaxisLabel: 'Users',
    yaxisLabel: 'Number of Cases',
    isAssignToEnabled: false,
    isCheckBoxEnabled: false,
    isExportEnabled: true,
    columns: [
        { name: 'id', title: 'Case ID', display: true },
        { name: 'serialnumber', title: 'Serial No.', display: true },
        { name: 'partnumber', title: 'Part No.', display: true },
        { name: 'description', title: 'Description', width: 100, display: true },
        { name: 'company', title: 'Company', display: true },

        { name: 'username', title: 'Name', width: 100, display: false },
        { name: 'opendate', title: 'Open Date', display: false },
        { name: 'updateddate', title: 'Updated Date', display: false },
        { name: 'closeddate', title: 'Closed Date', display: false },
        { name: 'productline', title: 'Product Name', width: 100, display: false },
        { name: 'model', title: 'Product Model', display: false },
        { name: 'warrantystatus', title: 'Warranty Status', display: false },
        { name: 'failuredescription', title: 'Failure Description', width: 100, display: false },
        { name: 'repairdescription', title: 'Detailed Description of service', width: 100, display: false },
        { name: 'symptom', title: 'Symptom', width: 100, display: false },
        { name: 'failure', title: 'Failure', width: 100, display: false },
        { name: 'servicepart', title: 'Service Part', width: 100, display: false },
        { name: 'servicepartserialnumber', title: 'ServicePart(S No./IMEI No.)', width: 100, display: false },
        { name: 'serviceoperation', title: 'Service Operation', width: 100, display: false },
        { name: 'repairtime', title: 'Repair Time', display: false },
        { name: 'age', title: 'Days Open', display: false },
        { name: 'stepstakentoverifyfailure', title: 'Steps taken to verify failure', width: 100, display: false },
        { name: 'productanalysis', title: 'Product Analysis', width: 100, display: false },
        { name: 'warrantylabour', title: 'Warranty Labour', display: false },
        { name: 'billablelabour', title: 'Billable Labour', display: false },
        { name: 'labourtype', title: 'Labour Type', display: false },
        { name: 'servicebulletin', title: 'Service Bulletin', width: 100, display: false },
        { name: 'gsbusinessarea', title: 'GS Business Area', width: 150, display: false },
        { name: 'swonumber', title: 'RMA-Repair Number', width: 150, display: false },
    ],
    filters () { return getFilters(serviceCaseSearchSuggestions, this.columns); },
    type: CategoryType.ServiceCase,
  },
  {
    id: 6,
    name: 'Users',
    moduleName: '',
      requestBodyforChart: '',
      requestBodyForExport: '',
    requestBodyForGrid: `query{
	      allusers %s {
              count,
              data  {
                    userid,
                    firstname,
                    lastname,
                    emailid,
                    status,
                    internaluser,
                    company {
                      companyname
                    },
                    userrolemapping{
                      userrole{
                        roleid,
                        rolename
                      }
                    },
                    userservicelevelmapping{
                    	serviceLevel{
                    		serviceLevelId,
                    		serviceLevelValue
                    	}
                    }
              }
	      }
      }`,
    isCheckBoxEnabled: true,
    isEditEnabled: true,
    isExportEnabled: false,
    isSelectOneOnly: true,
    columns: [
      { name: 'username', title: 'Name', width: 300, display: true },
      { name: 'email', title: 'Email Id', width: 300, display: true },
      { name: 'company', title: 'Company', width: 300, display: true },
      { name: 'usertype', title: 'User Type', width: 300, display: true },
      { name: 'roles', title: 'Roles', width: 300, display: true },
      { name: 'serviceLevel', title: 'Service Level', width: 300, display: true },
      { name: 'status', title: 'Status', width: 300, display: true }
    ],
    filters () {return getFilters(userSearchSuggestions, this.columns); },
    type: CategoryType.User
  },
  {
    id: 7,
    name: 'Roles',
    moduleName:'',
      requestBodyforChart: '',
      requestBodyForExport: '',
    requestBodyForGrid: `query{
	allroles %s
	{
        count,
		data{
			rolename
			roleid
			rolemodulemapping{
				module{
				modulename
				id
				}
			}
		}
	}
}`,
    isCheckBoxEnabled: true,
    isEditEnabled: true,
    isExportEnabled: false,
    isSelectOneOnly: true,
    columns: [
      { name: 'userrole', title: 'User Role', width: 300, display: true },
      { name: 'modules', title: 'Modules', width: 300, display: true }
    ],
    filters () { return getFilters(userroleSearchSuggestions, this.columns); },
    type: CategoryType.UserRole
  },
  {
    id: 8,
    moduleName: 'My Training Report',
    name: 'My Trainings',
    isDateDisabled: true,
      requestBodyforChart: '',
      requestBodyForExport: '',
    requestBodyForGrid: `query($category: String){ 
	trainings  (category: $category%s) { 
		count
        data{
            companyContactTwoDproductModelId,
            certificationname,
            coursename,  
            twodproductmodel{
                name
            },
            dateOfTraining,
            iscertified,
            isactive
        }  
	} 
}	`,
    requestBodyForGridTotalCount: `query($category: String){ 
      trainings  (category: $category%s) { 
        count             
      } 
    }	`,
    chartLabel: '',
    xaxisLabel: '',
    yaxisLabel: '',
    isAssignToEnabled: false,
    isCheckBoxEnabled: false,
    isExportEnabled: true,
    isOpenEnabled: false,
    columns: [
      { name: 'twodproductmodel', title: 'Product Model', display: true },      
      { name: 'coursename', title: 'Course Name', display: true },
      { name: 'certificationname', title: 'Certification Name', display: true },
      { name: 'dateoftraining', title: 'Date of Training', display: true },
      { name: 'iscertified', title: 'Certified', display: true },
      { name: 'isactive', title: 'Active', display: true }      
    ],
    filters () { return getFilters(trainingSearchSuggestions, this.columns); },
    type: CategoryType.TrainingAuthorization,
    route: authorizationRoute
  },
  {
    id: 9,
    moduleName: 'Team Training Report',
    name: 'Team Trainings',
    isDateDisabled: true,
      requestBodyforChart: '',
      requestBodyForExport: '',
    requestBodyforColChart: `query{ 
	                      userTrainingRelation 
	                      {
                          count
                              data{
                          firstName,
                          lastName,
                          email,
                          companyContactId,
                          numberOfAuthorizations
                        }  
	                      } 
                      }`,
    requestBodyfoPieChart:`query{ 
	productTrainingRelation  
	{ 
		count
        data{
            productLine,
            twodproductname,
            numberOfAuthorizations
        }  
	} 
}	`,
    requestBodyForGrid: `query($category: String){ 
	trainings  (category: $category%s) { 
		count
        data{
            companyContactTwoDproductModelId,
            certificationname,
            coursename,    
            companycontact{
                firstname,
                lastname,
                email
            },
            twodproductmodel{
                name
            },
            dateOfTraining,
            iscertified,
            isactive
        }  
	} 
}	`,
    requestBodyForGridTotalCount: `query($category: String){ 
      trainings  (category: $category%s) { 
        count  
      } 
    }	`,
    chartLabel: 'Number of Authorizations',
    xaxisLabel: 'Users',
    yaxisLabel: 'Number of Authorizations',
    isAssignToEnabled: false,
    isCheckBoxEnabled: false,
    isExportEnabled: true,
    isOpenEnabled: false,
    columns: [
      { name: 'name', title: 'Name', display: true },
      { name: 'email', title: 'Email', display: true },
      { name: 'twodproductmodel', title: 'Product Model', display: true },
      { name: 'coursename', title: 'Course Name', display: true },
      { name: 'certificationname', title: 'Certification Name', display: true },
      { name: 'dateoftraining', title: 'Date of Training', display: true },
      { name: 'iscertified', title: 'Certified', display: true },
      { name: 'isactive', title: 'Active', display: true }            
    ],
    filters () { return getFilters(trainingSearchSuggestions, this.columns); },
    type: CategoryType.TrainingAuthorization,
    route: teamTrainingRoute
  },
  {
    id: 11,
    moduleName: 'All Training Report',
    name: 'All Trainings',
    isDateDisabled: true,
      requestBodyforChart: '',
      requestBodyForExport: '',
    requestBodyforColChart:`query{ 
	companyTrainingRelation  
	{ 
		count
        data{
            companyId,
            companyName,
            numberOfAuthorizations
        }  
	} 
}	`,
    requestBodyfoPieChart: `query($category: String){ 
	productTrainingRelation  (category: $category)
	{ 
		count
        data{
            productLine,
            twodproductname,
            numberOfAuthorizations
        }  
	} 
}	`,
    requestBodyForGrid: `query($category: String){ 
	trainings  (category: $category%s) { 
		count
        data{
            companyContactTwoDproductModelId,
            certificationname,
            coursename,    
            companycontact{
                firstname,
                lastname,
                email,
            company{
                companyname
            }  
            },
            twodproductmodel{
                name
            },
            iscertified,
            isactive,
            dateOfTraining
        }  
	} 
}	`,
    requestBodyForGridTotalCount: `query($category: String){ 
      trainings  (category: $category%s) { 
        count 
      } 
    }	`,
    chartLabel: 'Company Authorizations',
    xaxisLabel: 'Companies',
    yaxisLabel: 'Number of Authorizations',
    isAssignToEnabled: false,
    isCheckBoxEnabled: false,
    isExportEnabled: true,
    isOpenEnabled: false,
    columns: [
      { name: 'name', title: 'Name', display: true },
      { name: 'email', title: 'Email', display: true },
      { name: 'company', title: 'Company', display: true },
      { name: 'twodproductmodel', title: 'Product Model', display: true },
      { name: 'coursename', title: 'Course Name', display: true },
      { name: 'certificationname', title: 'Certification Name', display: true },
      { name: 'dateoftraining', title: 'Date of Training', display: true },
      { name: 'iscertified', title: 'Certified', display: true },
      { name: 'isactive', title: 'Active', display: true }         
    ],
    filters () { return getFilters(trainingSearchSuggestions, this.columns); },
    type: CategoryType.TrainingAuthorization,
    route: alltrainingRoute
  },
  {
    id: 13,
    moduleName: 'Service Manuals',
    name: 'Service Manuals',
    type: CategoryType.ServiceManual,
    route: manualRoute,
    requestBodyforChart:'',
      requestBodyForGrid: '',
      requestBodyForExport: '',
    isExportEnabled: false,
    columns: [],
    filters: () => { }
    },
    {
        id: 14,
        moduleName: 'Service Bulletins',
        name: 'Service Bulletins',
        type: CategoryType.ServiceBulletin,
        route: bulletinRoute,
        requestBodyforChart: '',
        requestBodyForGrid: '',
        requestBodyForExport: '',
        isExportEnabled: false,
        columns: [],
        filters: () => { }
    },
    {
        id: 15,
        moduleName: 'General Service Information',
        name: 'General Service Information',
        type: CategoryType.GSInfo,
        route: GSInfoRoute,
        requestBodyforChart: '',
        requestBodyForExport: '',
        requestBodyForGrid: '',
        isExportEnabled: false,
        columns: [],
        filters: () => { }
    },
    {
        id: 16,
        moduleName: 'Downloads',
        name: 'Downloads',
        type: CategoryType.Downloads,
        route: downloadsRoute,
        requestBodyforChart: '',
        requestBodyForExport: '',
        requestBodyForGrid: '',
        isExportEnabled: false,
        isCheckBoxEnabled: true,
        columns: [
            { name: 'description', title: 'Description', display: true, width: 100 },
            { name: 'revision', title: 'Revision', display: true , width: 50},
            { name: 'releaseDate', title: 'Release Date', display: true, width: 50 },
            { name: 'comments', title: 'Comments', display: true, width: 300}
        ],
        filters () { return getFilters([], this.columns); },
    },
    {
        id: 17,
        moduleName: 'Labor Time Guide',
        name: 'Labor Time Guide',
        type: CategoryType.LaborTimeGuide,
        route: laborTimeGuideRoute,
        requestBodyforChart: '',
        requestBodyForGrid: '',
        requestBodyForExport: '',
        isExportEnabled: false,
        isCheckBoxEnabled: true,
        columns: [],
        filters () { return getFilters([], this.columns); }
    },
    {
        id: 18,
        moduleName: 'Instrument Constants',
        name: 'Instrument Constants',
        type: CategoryType.InstrumentConstants,
        route: instrumentConstantsRoute,
        requestBodyforChart: '',
        requestBodyForGrid: '',
        requestBodyForExport: '',
        isExportEnabled: false,
        columns: [
            { name: 'twodproductname', title: 'Product Name', display: true, width: 100 },
            { name: 'filename', title: 'File Name', display: true, width: 50 },
        ],
        filters () { return getFilters([], this.columns); },
    },

];
